import { Cell, Row, Workbook, Worksheet } from 'exceljs';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { get, groupBy, set } from 'lodash';
import { ContributorReportDto } from '@/models';
import { STYLES, NA, Style, CallbackFn } from './project-exporting-config';
import { addTableHeaderRow } from './project-exporting-helpers';
import { fetchContributorsReport } from './user';
import { sumByField } from '@/helpers';
import { ALL_TIME_VALUE } from '@/constants';

interface TopContributor extends ContributorReportDto {
  rank: number;
}

export default class ContributorsExport {
  private workbook = new Workbook();
  private ws: Worksheet;
  private topWs: Worksheet;
  private assessmentPeriodIds: number[] = [];
  private finalSelecteds: number[] = [];

  private columnWidths = [10, 30, 30, 15, 15, 15];
  private topColumnWidths = [10, 10, 30, 30, 15, 15, 15];

  constructor(selecteds: number[], finalSelecteds: number[]) {
    this.ws = this.workbook.addWorksheet('Contributors');
    this.topWs = this.workbook.addWorksheet('Top Contributors');
    this.assessmentPeriodIds = selecteds;
    this.finalSelecteds = finalSelecteds;
  }

  setColumnWidth(): void {
    this.ws.columns.forEach((column, index) => {
      column.width = this.columnWidths[index];
    });
    this.topWs.columns.forEach((column, index) => {
      column.width = this.topColumnWidths[index];
    });
  }

  addBorder(): void {
    this.ws.eachRow({ includeEmpty: true }, row => {
      row.eachCell({ includeEmpty: true }, cell => {
        const style = STYLES.thinGreyBorder;
        cell.border = {
          top: cell?.border?.top || style,
          bottom: cell?.border?.bottom || style,
          left: cell?.border?.left || style,
          right: cell?.border?.right || style
        };
      });
    });
    this.topWs.eachRow({ includeEmpty: true }, row => {
      row.eachCell({ includeEmpty: true }, cell => {
        const style = STYLES.thinGreyBorder;
        cell.border = {
          top: cell?.border?.top || style,
          bottom: cell?.border?.bottom || style,
          left: cell?.border?.left || style,
          right: cell?.border?.right || style
        };
      });
    });
  }

  addStyleToCell(cell: Cell, style: Style = {}): void {
    Object.keys(style).map(key => set(cell, key, get(style, key)));
  }

  addRow(ws: Worksheet, data: unknown[], style?: Style, callback?: CallbackFn): Row {
    const row = ws.addRow(data);
    row.eachCell((cell, index) => {
      if (typeof cell.value === 'number') {
        cell.alignment = STYLES.middleRightWrappedAlignment;
      } else {
        cell.alignment = STYLES.middleLeftWrappedAlignment;
      }
      if (cell.value === NA) {
        cell.font = STYLES.naFont;
        cell.alignment = STYLES.middleRightWrappedAlignment;
      }
      this.addStyleToCell(cell, style);
      if (callback) {
        callback(cell, index);
      }
    });
    return row;
  }

  getTop5Contributors(contributorsReport: ContributorReportDto[]): TopContributor[] {
    const groupByQuarter = groupBy(contributorsReport, 'quarter');
    return Object.entries(groupByQuarter).flatMap(([quarter, v]) => {
      const sortedContributors = v.sort((a, b) => b.periodAccumulatedDrops - a.periodAccumulatedDrops).slice(0, 5);
      return sortedContributors.map((contributor, idx) => {
        return {
          ...contributor,
          rank: idx + 1,
          quarter
        };
      });
    });
  }

  toContributorRowsData(contributorsReport: ContributorReportDto[]): unknown[][] {
    return contributorsReport.map(({ user, projectNames, periodAccumulatedDrops, hours, quarter }) => {
      return [user.code, user.fullName, projectNames?.join(', '), periodAccumulatedDrops, hours, quarter];
    });
  }

  toTopContributorRowsData(top5Contributors: TopContributor[]): unknown[][] {
    return top5Contributors.map(({ rank, user, projectNames, periodAccumulatedDrops, hours, quarter }) => {
      return [rank, user.code, user.fullName, projectNames?.join(', '), periodAccumulatedDrops, hours, quarter];
    });
  }

  addContributorInformationToWordSheet(contributorsReport: ContributorReportDto[]): void {
    const headers = [
      { title: 'ID' },
      { title: 'Name' },
      { title: 'Project name' },
      { title: 'Pollens', style: { fill: STYLES.lightOrangeFill } },
      { title: 'Hours', style: { fill: STYLES.lightOrangeFill } },
      { title: 'Quarter' }
    ];
    const ws = this.ws;
    addTableHeaderRow(ws, headers);
    const rows = this.toContributorRowsData(contributorsReport);
    rows.forEach(rowData => {
      this.addRow(ws, rowData);
    });
  }

  addTopContributorInformationToWordSheet(contributorsReport: ContributorReportDto[]): void {
    const headers = [
      { title: '#' },
      { title: 'ID' },
      { title: 'Name' },
      { title: 'Project name' },
      { title: 'Pollens', style: { fill: STYLES.lightOrangeFill } },
      { title: 'Hours', style: { fill: STYLES.lightOrangeFill } },
      { title: 'Quarter' }
    ];
    const ws = this.topWs;
    addTableHeaderRow(ws, headers);
    const top5Contributors = this.getTop5Contributors(contributorsReport);
    const rows = this.toTopContributorRowsData(top5Contributors);
    rows.forEach(rowData => {
      this.addRow(ws, rowData);
    });
  }

  getReportAllTimeData(contributorsReport: ContributorReportDto[]): ContributorReportDto[] {
    const groupByUser = groupBy(contributorsReport, 'user.id');
    return Object.entries(groupByUser).map(([_, v]) => {
      const user = v[0].user;
      const projectNames = [...new Set(v.flatMap(contributor => contributor.projectNames))];
      const periodAccumulatedDrops = sumByField(v, 'periodAccumulatedDrops');
      const hours = sumByField(v, 'hours');
      return {
        user,
        projectNames,
        periodAccumulatedDrops,
        hours,
        quarter: 'All time',
        assessmentPeriodId: v[0]?.assessmentPeriodId
      };
    });
  }

  async generateXLSXFile(): Promise<void> {
    const isAllTimeSelected = this.assessmentPeriodIds.indexOf(ALL_TIME_VALUE) > -1;
    const assessmentPeriodIds = this.finalSelecteds.filter(id => id !== ALL_TIME_VALUE);
    const { data } = await fetchContributorsReport({
      assessmentPeriodIds
    });
    let contributorsReport = data.filter(d => this.assessmentPeriodIds.includes(d.assessmentPeriodId));
    if (isAllTimeSelected) {
      contributorsReport = this.getReportAllTimeData(data).concat(contributorsReport);
    }
    this.addContributorInformationToWordSheet(contributorsReport);
    this.addTopContributorInformationToWordSheet(contributorsReport);
    this.addBorder();
    this.setColumnWidth();

    try {
      const buf = await this.workbook.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const currentDate = moment(new Date()).format('YYYYMMDD:HHmmss');
      saveAs(blob, `HIVE_Contributors_Export_${currentDate}.xlsx`);
    } catch (error) {
      console.log(error);
    }
  }
}
